import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, Cable, CheckCircle2, Power, RefreshCw, ShieldAlert, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

interface Waveshare8CHDashboardProps {
  clientId: string;
  token: string;
  pollingIntervalMs?: number;
}

const CHANNELS = Array.from({ length: 8 }, (_, index) => index + 1);

export function Waveshare8CHDashboard({ pollingIntervalMs = 500 }: Waveshare8CHDashboardProps) {
  const [inputs, setInputs] = useState<boolean[]>([true, false, false, true, false, true, false, false]);
  const [outputs, setOutputs] = useState<boolean[]>(Array(8).fill(false));
  const [connected, setConnected] = useState(false);
  const [config, setConfig] = useState({
    port: 'COM3',
    baudRate: '9600',
    dataBits: '8',
    parity: 'none',
    stopBits: '1',
    slaveId: '1',
    pollIntervalMs: String(pollingIntervalMs || 500),
  });

  const activeInputs = useMemo(() => inputs.filter(Boolean).length, [inputs]);
  const activeOutputs = useMemo(() => outputs.filter(Boolean).length, [outputs]);

  const toggleOutput = (channel: number) => {
    setOutputs((prev) => prev.map((value, index) => (index === channel - 1 ? !value : value)));
  };

  const toggleInputPreview = (channel: number) => {
    setInputs((prev) => prev.map((value, index) => (index === channel - 1 ? !value : value)));
  };

  const setAllOutputs = (state: boolean) => {
    setOutputs(Array(8).fill(state));
  };

  return (
    <div className="space-y-5">
      <section className="rounded-lg border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <Cable className="h-4 w-4 text-emerald-500" />
              Modbus RTU IO Module
            </div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">Waveshare 8CH Command Center</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Dedicated dashboard for Waveshare Modbus RTU IO 8CH with 8 digital inputs and 8 digital outputs.
            </p>
          </div>

          <div className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${connected ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-amber-500/30 bg-amber-500/10'}`}>
            <span className={`h-3 w-3 rounded-full ${connected ? 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.8)]' : 'bg-amber-500'}`} />
            <div>
              <p className="text-sm font-semibold">{connected ? 'Connected' : 'Offline Preview'}</p>
              <p className="text-xs text-muted-foreground">Slave {config.slaveId} / {config.baudRate} baud</p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-4">
        {[
          { label: 'Digital Inputs', value: `${activeInputs} / 8`, icon: Activity, tone: 'text-emerald-500' },
          { label: 'Digital Outputs', value: `${activeOutputs} / 8`, icon: Power, tone: 'text-cyan-500' },
          { label: 'Poll Cycle', value: `${config.pollIntervalMs} ms`, icon: RefreshCw, tone: 'text-violet-500' },
          { label: 'Safety State', value: connected ? 'Live' : 'Preview', icon: ShieldAlert, tone: 'text-amber-500' },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <Card key={item.label}>
              <CardContent className="flex items-center justify-between p-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{item.label}</p>
                  <p className="mt-1 text-2xl font-bold">{item.value}</p>
                </div>
                <Icon className={`h-6 w-6 ${item.tone}`} />
              </CardContent>
            </Card>
          );
        })}
      </section>

      <section className="grid gap-5 xl:grid-cols-[360px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Connection</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="waveshare-port">COM Port</Label>
                <Input id="waveshare-port" value={config.port} onChange={(event) => setConfig({ ...config, port: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="waveshare-baud">Baud Rate</Label>
                <Input id="waveshare-baud" value={config.baudRate} onChange={(event) => setConfig({ ...config, baudRate: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="waveshare-data-bits">Data Bits</Label>
                <Input id="waveshare-data-bits" value={config.dataBits} onChange={(event) => setConfig({ ...config, dataBits: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="waveshare-parity">Parity</Label>
                <Input id="waveshare-parity" value={config.parity} onChange={(event) => setConfig({ ...config, parity: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="waveshare-stop-bits">Stop Bits</Label>
                <Input id="waveshare-stop-bits" value={config.stopBits} onChange={(event) => setConfig({ ...config, stopBits: event.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="waveshare-slave">Slave ID</Label>
                <Input id="waveshare-slave" value={config.slaveId} onChange={(event) => setConfig({ ...config, slaveId: event.target.value })} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="waveshare-poll">Poll Interval</Label>
              <Input id="waveshare-poll" value={config.pollIntervalMs} onChange={(event) => setConfig({ ...config, pollIntervalMs: event.target.value })} />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">Preview connection</p>
                <p className="text-xs text-muted-foreground">Use until bridge write support is attached</p>
              </div>
              <Switch checked={connected} onCheckedChange={setConnected} />
            </div>
          </CardContent>
        </Card>

        <div className="space-y-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Digital Inputs</CardTitle>
              <span className="text-xs font-semibold text-muted-foreground">FC02 / Address 0x0000</span>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {CHANNELS.map((channel) => (
                  <motion.button
                    key={`di-${channel}`}
                    type="button"
                    whileHover={{ y: -2 }}
                    onClick={() => toggleInputPreview(channel)}
                    className={`rounded-lg border p-4 text-left transition-colors ${inputs[channel - 1] ? 'border-emerald-500/40 bg-emerald-500/10' : 'bg-muted/20 hover:bg-muted/40'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold">DI{channel}</span>
                      <span className={`h-3 w-3 rounded-full ${inputs[channel - 1] ? 'bg-emerald-500' : 'bg-muted-foreground/30'}`} />
                    </div>
                    <p className={`mt-3 text-2xl font-bold ${inputs[channel - 1] ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                      {inputs[channel - 1] ? 'ON' : 'OFF'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">Input channel {channel}</p>
                  </motion.button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-base">Digital Outputs</CardTitle>
                <p className="mt-1 text-xs text-muted-foreground">FC01 / FC05 / FC0F output control surface</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setAllOutputs(false)}>All Off</Button>
                <Button size="sm" onClick={() => setAllOutputs(true)}>All On</Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {CHANNELS.map((channel) => {
                  const active = outputs[channel - 1];
                  return (
                    <motion.div
                      key={`do-${channel}`}
                      whileHover={{ y: -2 }}
                      className={`rounded-lg border p-4 transition-colors ${active ? 'border-cyan-500/40 bg-cyan-500/10' : 'bg-muted/20'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">DO{channel}</span>
                        {active ? <ToggleRight className="h-5 w-5 text-cyan-500" /> : <ToggleLeft className="h-5 w-5 text-muted-foreground" />}
                      </div>
                      <p className={`mt-3 text-2xl font-bold ${active ? 'text-cyan-500' : 'text-muted-foreground'}`}>
                        {active ? 'ON' : 'OFF'}
                      </p>
                      <Button className="mt-4 w-full" variant={active ? 'outline' : 'default'} size="sm" onClick={() => toggleOutput(channel)}>
                        {active ? 'Turn Off' : 'Turn On'}
                      </Button>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-3 p-4 text-sm text-muted-foreground">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <span>Module added as a separate dashboard feature. Hardware write/read wiring can be connected to the bridge command layer next.</span>
        </CardContent>
      </Card>
    </div>
  );
}
